import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  FiClock, FiPlay, FiPause, FiSquare, FiCheck, FiX,
  FiCalendar, FiUser, FiDollarSign, FiAlertCircle, FiDownload,
  FiUploadCloud, FiEye, FiChevronDown, FiChevronUp, FiUsers,
  FiBriefcase, FiCreditCard, FiMail
} from 'react-icons/fi';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TimesheetManagement = () => {
  const { user } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTimesheet, setActiveTimesheet] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [onBreak, setOnBreak] = useState(false);
  
  // Payroll state
  const [showPayroll, setShowPayroll] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [payrollPeriod, setPayrollPeriod] = useState('weekly');
  const [showPayslipPreview, setShowPayslipPreview] = useState(false);
  const [previewPayslip, setPreviewPayslip] = useState(null);

  useEffect(() => {
    if (user) {
      fetchTimesheets();
      if (user.role === 'employee' || user.role === 'careWorker') {
        checkActiveTimesheet();
      }
    }
  }, [selectedPeriod, selectedStatus, user]);

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = {
        period: selectedPeriod,
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
      };

      const response = await axios.get(`${API_URL}/api/timesheets`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // Ensure response.data is an array
      const data = Array.isArray(response.data) ? response.data : [];
      setTimesheets(data);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load timesheets');
      setTimesheets([]);
    } finally {
      setLoading(false);
    }
  };

  const checkActiveTimesheet = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/timesheets/active/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveTimesheet(response.data);
      
      // Check if on break
      if (response.data && response.data.breakStart.length > response.data.breakEnd.length) {
        setOnBreak(true);
      }
    } catch (error) {
      console.error('Error checking active timesheet:', error);
    }
  };

  const handleClockIn = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/timesheets/clock-in`,
        {
          method: 'web',
          location: {
            address: 'Web Portal',
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setActiveTimesheet(response.data);
      fetchTimesheets();
    } catch (error) {
      console.error('Error clocking in:', error);
      alert(error.response?.data?.message || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/timesheets/${activeTimesheet._id}/clock-out`,
        {
          method: 'web',
          location: {
            address: 'Web Portal',
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setActiveTimesheet(null);
      setOnBreak(false);
      fetchTimesheets();
    } catch (error) {
      console.error('Error clocking out:', error);
      alert(error.response?.data?.message || 'Failed to clock out');
    }
  };

  const handleBreakStart = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/timesheets/${activeTimesheet._id}/break-start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOnBreak(true);
      checkActiveTimesheet();
    } catch (error) {
      console.error('Error starting break:', error);
    }
  };

  const handleBreakEnd = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/timesheets/${activeTimesheet._id}/break-end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOnBreak(false);
      checkActiveTimesheet();
    } catch (error) {
      console.error('Error ending break:', error);
    }
  };

  const handleApprove = async (timesheetId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/timesheets/${timesheetId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchTimesheets();
    } catch (error) {
      console.error('Error approving timesheet:', error);
    }
  };

  const handleReject = async (timesheetId) => {
    const notes = prompt('Please provide a reason for rejection:');
    if (!notes) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/timesheets/${timesheetId}/reject`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchTimesheets();
    } catch (error) {
      console.error('Error rejecting timesheet:', error);
    }
  };

  const exportPayrollReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const response = await axios.get(`${API_URL}/api/timesheets/report/payroll`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        },
      });

      // Convert to CSV
      const data = response.data;
      const csv = [
        ['Employee', 'Regular Hours', 'Overtime Hours', 'Double Time Hours', 'Total Hours', 'Regular Pay', 'Overtime Pay', 'Double Time Pay', 'Total Pay'],
        ...data.map(row => [
          `${row.user.firstName} ${row.user.lastName}`,
          row.regularHours.toFixed(2),
          row.overtimeHours.toFixed(2),
          row.doubleTimeHours.toFixed(2),
          row.totalHours.toFixed(2),
          `£${row.regularPay.toFixed(2)}`,
          `£${row.overtimePay.toFixed(2)}`,
          `£${row.doubleTimePay.toFixed(2)}`,
          `£${row.totalPay.toFixed(2)}`,
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting payroll report:', error);
    }
  };

  const exportToNourish = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!Array.isArray(timesheets)) {
        console.error('Timesheets is not an array');
        return;
      }
      const approvedTimesheets = timesheets.filter(ts => ts.status === 'approved');
      
      const nourishData = {
        exportDate: new Date().toISOString(),
        period: selectedPeriod,
        employees: approvedTimesheets.map(ts => ({
          employeeId: ts.user._id,
          employeeName: `${ts.user.firstName} ${ts.user.lastName}`,
          date: ts.date,
          clockIn: ts.clockIn?.time,
          clockOut: ts.clockOut?.time,
          totalHours: ts.totalHoursWorked,
          regularHours: ts.regularHours || ts.totalHoursWorked,
          overtimeHours: ts.overtimeHours || 0,
          breakMinutes: ts.totalBreakMinutes || 0,
          totalPay: ts.totalPay,
          status: ts.status
        }))
      };

      const blob = new Blob([JSON.stringify(nourishData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nourish-export-${format(new Date(), 'yyyy-MM-dd')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Nourish:', error);
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handlePreviewPayslip = (timesheet) => {
    setPreviewPayslip(timesheet);
    setShowPayslipPreview(true);
  };

  const calculatePayrollSummary = () => {
    if (!Array.isArray(timesheets) || timesheets.length === 0) {
      return {
        totalEmployees: 0,
        totalHours: '0.00',
        totalPay: '0.00',
        selectedCount: 0
      };
    }

    const approvedTimesheets = timesheets.filter(ts => ts.status === 'approved');
    const totalEmployees = new Set(approvedTimesheets.map(ts => ts.user._id)).size;
    const totalHours = approvedTimesheets.reduce((sum, ts) => sum + (ts.totalHoursWorked || 0), 0);
    const totalPay = approvedTimesheets.reduce((sum, ts) => sum + (ts.totalPay || 0), 0);
    
    return {
      totalEmployees,
      totalHours: totalHours.toFixed(2),
      totalPay: totalPay.toFixed(2),
      selectedCount: selectedEmployees.length
    };
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 border border-yellow-200',
      approved: 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200',
      rejected: 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200',
      paid: 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'HH:mm');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd MMM yyyy');
  };

  if (loading && !activeTimesheet) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading timesheets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiAlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Timesheets</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null);
              fetchTimesheets();
            }}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiAlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please log in to view your timesheets.</p>
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const payrollSummary = isAdmin ? calculatePayrollSummary() : null;

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 via-blue-50/30 to-blue-100/20 min-h-screen">
      {/* Modern Header with Gradient */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-blue-500 to-blue-700 bg-clip-text text-transparent">
              Time Sheets
            </h1>
            <p className="text-gray-600 mt-2 flex items-center">
              <FiAlertCircle className="mr-2" />
              Daily work hours and task progress
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={exportToNourish}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
              >
                <FiUploadCloud className="mr-2" />
                Export to Nourish
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowPayroll(!showPayroll)}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
              >
                {showPayroll ? <FiChevronUp className="mr-2" /> : <FiChevronDown className="mr-2" />}
                {showPayroll ? 'Hide' : 'Show'} Payroll
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Modern Clock In/Out Panel for Employees */}
      {!isAdmin && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-8 overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-100/50 to-purple-100/50 rounded-full blur-3xl -z-0"></div>
          
          <div className="relative z-10">
            {activeTimesheet ? (
              <div className="space-y-6">
                <div className="flex items-center text-green-600 mb-4">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    <FiClock className="h-8 w-8 mr-3" />
                  </motion.div>
                  <span className="text-2xl font-bold">Currently Clocked In</span>
                </div>
                
                {/* Stat Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <p className="text-blue-700 text-sm font-semibold mb-2">Clock In Time</p>
                    <p className="text-3xl font-bold text-blue-900">{formatTime(activeTimesheet.clockIn?.time)}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-amber-100 p-6 rounded-xl border border-orange-200">
                    <p className="text-orange-700 text-sm font-semibold mb-2">Total Breaks</p>
                    <p className="text-3xl font-bold text-orange-900">{activeTimesheet.totalBreakMinutes || 0} mins</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                    <p className="text-purple-700 text-sm font-semibold mb-2">Current Status</p>
                    <p className="text-3xl font-bold text-purple-900">{onBreak ? 'On Break' : 'Working'}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  {onBreak ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleBreakEnd}
                      className="flex-1 flex items-center justify-center px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-xl transition-all text-lg font-bold"
                    >
                      <FiPlay className="mr-2 h-5 w-5" />
                      End Break
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleBreakStart}
                      className="flex-1 flex items-center justify-center px-8 py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl hover:shadow-xl transition-all text-lg font-bold"
                    >
                      <FiPause className="mr-2 h-5 w-5" />
                      Start Break
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleClockOut}
                    className="flex-1 flex items-center justify-center px-8 py-4 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:shadow-xl transition-all text-lg font-bold"
                  >
                    <FiSquare className="mr-2 h-5 w-5" />
                    Clock Out
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to Start Your Shift?</h3>
                  <p className="text-gray-600">Clock in to begin tracking your time</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleClockIn}
                  className="flex items-center px-12 py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-2xl transition-all text-xl font-bold"
                >
                  <FiPlay className="mr-3 h-6 w-6" />
                  Clock In
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Payroll Processing Section */}
      <AnimatePresence>
        {isAdmin && showPayroll && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
                Payroll Processing
              </h2>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-700 text-sm font-semibold mb-1">Total Employees</p>
                      <p className="text-3xl font-bold text-blue-900">{payrollSummary.totalEmployees}</p>
                    </div>
                    <FiUsers className="h-10 w-10 text-blue-600" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-6 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-700 text-sm font-semibold mb-1">Total Hours</p>
                      <p className="text-3xl font-bold text-green-900">{payrollSummary.totalHours}</p>
                    </div>
                    <FiClock className="h-10 w-10 text-green-600" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-700 text-sm font-semibold mb-1">Total Pay</p>
                      <p className="text-3xl font-bold text-purple-900">£{payrollSummary.totalPay}</p>
                    </div>
                    <FiDollarSign className="h-10 w-10 text-purple-600" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-amber-100 p-6 rounded-xl border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-700 text-sm font-semibold mb-1">Selected</p>
                      <p className="text-3xl font-bold text-orange-900">{payrollSummary.selectedCount}</p>
                    </div>
                    <FiCheck className="h-10 w-10 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* Payroll Period Selection */}
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Payroll Period</label>
                  <select
                    value={payrollPeriod}
                    onChange={(e) => setPayrollPeriod(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold"
                  >
                    <option value="weekly">Weekly (Monday - Sunday)</option>
                    <option value="monthly">Monthly (1st - Last Day)</option>
                  </select>
                </div>

                <div className="flex items-end gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center"
                  >
                    <FiCreditCard className="mr-2" />
                    Process Payment
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={exportPayrollReport}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center"
                  >
                    <FiDownload className="mr-2" />
                    Export Report
                  </motion.button>
                </div>
              </div>

              {/* Employee Selection List */}
              <div className="bg-gray-50 rounded-xl p-4 max-h-64 overflow-y-auto">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Employees for Payroll</h3>
                <div className="space-y-2">
                  {Array.isArray(timesheets) && timesheets
                    .filter(ts => ts.status === 'approved')
                    .reduce((unique, ts) => {
                      if (!unique.find(u => u.user._id === ts.user._id)) {
                        unique.push(ts);
                      }
                      return unique;
                    }, [])
                    .map((timesheet) => (
                      <motion.div
                        key={timesheet.user._id}
                        whileHover={{ scale: 1.01 }}
                        className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all ${
                          selectedEmployees.includes(timesheet.user._id)
                            ? 'bg-blue-100 border-2 border-blue-400'
                            : 'bg-white border-2 border-gray-200 hover:border-blue-300'
                        }`}
                        onClick={() => toggleEmployeeSelection(timesheet.user._id)}
                      >
                        <div className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(timesheet.user._id)}
                            onChange={() => {}}
                            className="h-5 w-5 text-blue-600 rounded mr-4"
                          />
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold mr-4">
                            {timesheet.user.firstName?.[0]}{timesheet.user.lastName?.[0]}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {timesheet.user.firstName} {timesheet.user.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{timesheet.user.emailId}</p>
                          </div>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewPayslip(timesheet);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <FiEye className="h-5 w-5" />
                        </motion.button>
                      </motion.div>
                    ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modern Filters Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 mb-6"
      >
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Period</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="all">All Time</option>
            </select>
          </div>
          {isAdmin && (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          )}
        </div>
      </motion.div>

      {/* Modern Timesheets Table */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                  Date
                </th>
                {isAdmin && (
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                    Employee
                  </th>
                )}
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                  Clock In/Out
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                  Break
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                  Hours
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                  Pay
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                  Compliance
                </th>
                {isAdmin && (
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {!Array.isArray(timesheets) || timesheets.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 9 : 7} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <FiBriefcase className="h-12 w-12 text-gray-300 mb-3" />
                      <p className="text-lg font-semibold">No timesheets found</p>
                      <p className="text-sm">for the selected period</p>
                    </div>
                  </td>
                </tr>
              ) : (
                Array.isArray(timesheets) && timesheets.map((timesheet, index) => (
                  <motion.tr
                    key={timesheet._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm">
                        <FiCalendar className="text-blue-500 mr-2" />
                        <span className="font-semibold">{formatDate(timesheet.date)}</span>
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold mr-3">
                            {timesheet.user?.firstName?.[0]}{timesheet.user?.lastName?.[0]}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {timesheet.user?.firstName} {timesheet.user?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{timesheet.user?.emailId}</p>
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="space-y-1">
                        <div className="flex items-center text-green-600 font-semibold">
                          <FiClock className="mr-1" />
                          {formatTime(timesheet.clockIn?.time)}
                        </div>
                        {timesheet.clockOut?.time && (
                          <div className="flex items-center text-red-600 font-semibold">
                            <FiClock className="mr-1" />
                            {formatTime(timesheet.clockOut?.time)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
                        {timesheet.totalBreakMinutes || 0} mins
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-bold text-gray-900 text-lg">
                          {timesheet.totalHoursWorked?.toFixed(2) || '0.00'} hrs
                        </div>
                        {timesheet.overtimeHours > 0 && (
                          <div className="text-xs text-orange-600 font-semibold">
                            + {timesheet.overtimeHours.toFixed(2)} OT
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-bold text-green-600 text-lg">
                          £{timesheet.totalPay?.toFixed(2) || '0.00'}
                        </div>
                        {timesheet.overtimePay > 0 && (
                          <div className="text-xs text-gray-500">
                            (£{timesheet.overtimePay.toFixed(2)} OT)
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(timesheet.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {timesheet.restPeriodCompliance === false ? (
                        <div className="flex items-center text-red-600 text-xs font-semibold">
                          <FiAlertCircle className="mr-1" />
                          <span>Issue</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-green-600 text-xs font-semibold">
                          <FiCheck className="mr-1" />
                          <span>Compliant</span>
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {timesheet.status === 'pending' && (
                          <div className="flex justify-end gap-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleApprove(timesheet._id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Approve"
                            >
                              <FiCheck className="h-5 w-5" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleReject(timesheet._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Reject"
                            >
                              <FiX className="h-5 w-5" />
                            </motion.button>
                          </div>
                        )}
                      </td>
                    )}
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Payslip Preview Modal */}
      <AnimatePresence>
        {showPayslipPreview && previewPayslip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowPayslipPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold">Payslip Preview</h2>
                    <p className="text-blue-100 mt-1">
                      {payrollPeriod === 'weekly' ? 'Week ending' : 'Month ending'} {formatDate(previewPayslip.date)}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPayslipPreview(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {/* Employee Details */}
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Employee Details</h3>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-semibold">{previewPayslip.user?.firstName} {previewPayslip.user?.lastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold">{previewPayslip.user?.emailId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Bank Account</p>
                    <p className="font-semibold">{previewPayslip.user?.bankDetails?.accountName || 'Not Set'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Account Number</p>
                    <p className="font-semibold">
                      {previewPayslip.user?.bankDetails?.accountNumber 
                        ? `**** ${previewPayslip.user.bankDetails.accountNumber.slice(-4)}`
                        : 'Not Set'}
                    </p>
                  </div>
                </div>
                
                {/* Earnings */}
                <div className="mt-6">
                  <h3 className="text-lg font-bold mb-4">Earnings</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between p-4 bg-gray-50 rounded-xl">
                      <span>Regular Hours ({previewPayslip.totalHoursWorked?.toFixed(2) || '0.00'} hrs)</span>
                      <span className="font-semibold">£{previewPayslip.totalPay?.toFixed(2) || '0.00'}</span>
                    </div>
                    {previewPayslip.overtimeHours > 0 && (
                      <div className="flex justify-between p-4 bg-orange-50 rounded-xl">
                        <span>Overtime ({previewPayslip.overtimeHours?.toFixed(2)} hrs)</span>
                        <span className="font-semibold text-orange-600">£{previewPayslip.overtimePay?.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Total */}
                <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border-2 border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold">Net Pay</span>
                    <span className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                      £{previewPayslip.totalPay?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center justify-center"
                  >
                    <FiMail className="mr-2" />
                    Send Payslip
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center justify-center"
                  >
                    <FiDownload className="mr-2" />
                    Download PDF
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimesheetManagement;
